// hooks/useRoleManagementLogic.js
import Icon from 'components/appIcon';
import Button from 'components/ui/button';
import { useState, useEffect } from 'react';
import roleApi from 'services/api/roleApi';

const useRoleManagementLogic = () => {
    const [isNavCollapsed, setIsNavCollapsed] = useState(false);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalState, setModalState] = useState({
        isOpen: false,
        role: null,
        mode: "add",
    });
    const [notification, setNotification] = useState(null);
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        is_active: true,
    });
    const [errors, setErrors] = useState({});

    // Notification functions
    const closeNotification = () => {
        setNotification(null);
    };

    const showNotification = (message, type = "info") => {
        setNotification({ message, type });
    };

    // Data loading function
    const loadRoles = async () => {
        try {
            setLoading(true);
            const rolesData = await roleApi.getRoles();
            setRoles(rolesData);
        } catch (error) {
            console.error("Error fetching roles:", error);
            showNotification("Failed to fetch roles", "error");
        } finally {
            setLoading(false);
        }
    };

    // Load data on component mount
    useEffect(() => {
        loadRoles();
    }, []);

    // Form handling functions
    const handleInputChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: "" }));
        }
    };

    const validateForm = () => {
        const validationErrors = {};
        if (!formData.name.trim()) validationErrors.name = "Role name is required";
        setErrors(validationErrors);
        return Object.keys(validationErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            showNotification("Please fix errors before saving", "error");
            return;
        }

        try {
            if (modalState.mode === "add") {
                await roleApi.createRole(formData);
                showNotification("Role created successfully", "success");
            } else {
                await roleApi.updateRole(modalState.role.id, formData);
                showNotification("Role updated successfully", "success");
            }
            setModalState({ isOpen: false, role: null, mode: "add" });
            loadRoles();
        } catch (error) {
            console.error("Error saving role:", error);
            const errorMessage = error.response?.data?.detail || "Failed to save role";
            showNotification(errorMessage, "error");
        }
    };

    const handleAddRole = () => {
        setFormData({
            name: "",
            description: "",
            is_active: true,
        });
        setErrors({});
        setModalState({ isOpen: true, role: null, mode: "add" });
    };

    const handleEditRole = (role) => {
        setFormData({
            name: role.name,
            description: role.description || "",
            is_active: role.is_active,
        });
        setErrors({});
        setModalState({ isOpen: true, role, mode: "edit" });
    };

    const handleDeleteRole = async (role) => {
        if (window.confirm(`Are you sure you want to delete "${role.name}"?`)) {
            try {
                await roleApi.deleteRole(role.id);
                showNotification("Role deleted successfully", "success");
                loadRoles();
            } catch (error) {
                console.error("Error deleting role:", error);
                const errorMessage = error.response?.data?.detail || "Failed to delete role";
                showNotification(errorMessage, "error");
            }
        }
    };

    // Table columns definition
    const columns = [
        {
            header: "Name",
            accessor: "name",
            cell: (row) => <span className="font-medium">{row.name}</span>,
        },
        {
            header: "Description",
            accessor: "description",
            cell: (row) => <span>{row.description || "No description"}</span>,
        },
        {
            header: "Status",
            accessor: "is_active",
            cell: (row) => (
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${row.is_active
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}>
                    {row.is_active ? "Active" : "Inactive"}
                </span>
            ),
        },
        {
            header: "Created At",
            accessor: "created_at",
            cell: (row) => (
                <span>
                    {row.created_at ? new Date(row.created_at).toLocaleDateString() : "N/A"}
                </span>
            ),
        },
        {
            header: "Actions",
            accessor: "actions",
            cell: (row) => (
                <div className="flex space-x-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditRole(row)}
                        className="p-1.5"
                    >
                        <Icon name="Edit" size={16} />
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteRole(row)}
                        className="p-1.5 text-red-500 hover:text-red-700"
                    >
                        <Icon name="Trash" size={16} />
                    </Button>
                </div>
            ),
        },
    ];

    return {
        // State
        isNavCollapsed,
        roles,
        loading,
        modalState,
        notification,
        formData,
        errors,
        columns,

        // State setters
        setIsNavCollapsed,
        setModalState,

        // Functions
        closeNotification,
        showNotification,
        loadRoles,
        handleInputChange,
        validateForm,
        handleSave,
        handleAddRole,
        handleEditRole,
        handleDeleteRole
    };
};

export default useRoleManagementLogic;